/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The four things an agency asks about its subscription.
 *
 * What it is, what it costs, when it is charged, and on what card. The old
 * strip answered the first two with one plan name and one price, which is
 * the shape of a subscription that is one tier for everybody.
 */

import type { SubscriptionLine } from '../seats/seat-reduction';
import type { ActiveDiscount } from '@services/requests/housien-services/AgenciesService.subscription';

/** One plan on the subscription, as a chip. */
export interface PlanChip {
  planId: string;
  label: string;
  seats: number;
}

/** The chips the plan column shows, in the order the lines were bought. */
export const planChips = (
  items: SubscriptionLine[], titleOf: (planId: string) => string | null,
): PlanChip[] => (items || [])
  .filter((line) => line.quantity > 0)
  .map((line) => ({
    planId: line.plan_id,
    label: titleOf(line.plan_id) ?? line.plan_id,
    seats: line.quantity,
  }));

/**
 * What the subscription costs per cycle, from what it is made of.
 *
 * Summed from the lines rather than read off one plan: the billing snapshot
 * reports a single price, and on a team that mixes tiers that price is one
 * line's, not the bill.
 */
export const cycleTotalCents = (
  items: SubscriptionLine[], priceOf: (planId: string) => number,
): number => (items || []).reduce(
  (total, line) => total + priceOf(line.plan_id) * Math.max(0, line.quantity), 0,
);

/**
 * What a yearly subscription works out to per month.
 *
 * The yearly price is already the discounted one, so this divides rather
 * than discounting again — quoting the monthly price minus 16% would be a
 * number the agency is not paying.
 */
export const monthlyEquivalentCents = (
  cycleCents: number, frequency: string | null,
): number => (frequency === 'yearly' ? Math.round(cycleCents / 12) : cycleCents);

/** Days until the next charge, never negative. */
export const daysUntilCharge = (
  isoDate: string | null | undefined, now: Date,
): number | null => {
  if (!isoDate) return null;
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.ceil((then - now.getTime()) / 86400000));
};

/** How many seats a set of subscription lines adds up to. */
export const seatCount = (
  items: SubscriptionLine[] | null | undefined,
): number => (items ?? []).reduce(
  (total, line) => total + Math.max(0, line.quantity), 0,
);

/**
 * How many seats the subscription will hold after a scheduled reduction.
 *
 * The next-charge column quotes the bill that is actually coming, so it has
 * to count the seats that will exist then and not the ones that exist now.
 */
export const seatsAtRenewal = (
  items: SubscriptionLine[],
  scheduled: { items: SubscriptionLine[] } | null | undefined,
): number => seatCount(scheduled?.items ?? items);

/**
 * Whether a coupon still covers the renewal that is coming.
 *
 * `remaining_cycles` is a sentinel and not a count, so this reads it rather
 * than comparing it to zero in one direction: `-1` is a coupon that never
 * runs out and would fail a `> 0` test, and `0` is one already spent.
 *
 * @param discount - The coupon Payien reports, when there is one.
 * @returns Whether the next charge is discounted.
 */
export const discountApplies = (
  discount: ActiveDiscount | null | undefined,
): boolean => Boolean(discount) && (discount?.remaining_cycles ?? 0) !== 0;

/**
 * The amount after the coupon, mirroring what Payien will actually charge.
 *
 * The same arithmetic as the renewal worker, deliberately: this screen quotes
 * a figure the agency will see on its card, and a prettier rounding here
 * would be a number that does not match the invoice. Cents are truncated, not
 * rounded, and the result never goes below zero.
 *
 * Payien discounts the plan total and bills addons in full; the total handed
 * in here is the seats total, which is what the coupon applies to.
 *
 * @param cents - The undiscounted total for the cycle.
 * @param discount - The coupon Payien reports, when there is one.
 * @returns The amount the next charge will be.
 */
export const discountedCents = (
  cents: number, discount: ActiveDiscount | null | undefined,
): number => {
  if (!discountApplies(discount)) return cents;
  const value = discount?.discount_value ?? 0;
  // Both spellings, as Payien accepts both: the coupon is frozen with whatever
  // the checkout session carried, and reading only one of them would quote the
  // full price for half the coupons in the catalogue.
  const kind = (discount?.discount_type ?? '').toLowerCase();
  const priced: Record<string, number> = {
    free: 0,
    percent: Math.trunc(cents * (1 - value / 100)),
    percentage: Math.trunc(cents * (1 - value / 100)),
    fixed: cents - Math.trunc(value),
  };
  // A kind of coupon this screen does not know how to price falls back to the
  // full amount: inventing a discount would promise a bill Payien is not
  // going to issue.
  return Math.max(0, priced[kind] ?? cents);
};

/**
 * What the next charge will be, after a scheduled reduction and any coupon.
 *
 * The coupon is counted here rather than left to the caller because this is
 * the figure the agency reads as "what I pay next time". Quoted from the
 * catalogue alone, a subscription bought with a coupon that still has cycles
 * to run was shown a price nobody was going to be charged.
 *
 * @param items - What the subscription is made of today.
 * @param scheduled - The smaller list that replaces them at the renewal.
 * @param priceOf - The catalogue price per seat of a plan.
 * @param discount - The coupon Payien reports, when there is one.
 * @returns The amount of the next charge.
 */
export const nextChargeCents = (
  items: SubscriptionLine[],
  scheduled: { items: SubscriptionLine[] } | null | undefined,
  priceOf: (planId: string) => number,
  discount?: ActiveDiscount | null,
): number => discountedCents(
  cycleTotalCents(scheduled?.items ?? items ?? [], priceOf), discount,
);
